import { Modal, useModals } from 'almanac-ui/Modal'
import { Text } from 'blaze-ui/Text/Text'
import { GoHighLevelIntegration, GoHighLevelProfile } from 'api/clients/ExternalIntegrationsClient'
import { GoHighLevelBlogSelect } from 'components/BlogMetadata/components/integrations/GoHighLevelBlogSelect/GoHighLevelBlogSelect'
import { GoHighLevelAuthorSelect } from 'components/BlogMetadata/components/integrations/GoHighLevelAuthorSelect/GoHighLevelAuthorSelect'
import { useUpdateExternalIntegration } from 'hooks/queries/externalIntegrations/useUpdateExternalIntegration'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useState, useEffect } from 'react'
import Styles from './GoHighLevelConfigurationModal.module.scss'
import { Heading } from 'blaze-ui/Heading/Heading'

interface Props {
  integration: GoHighLevelIntegration
  onClose?: () => void
  onPressOutside?: () => void
}

export const GoHighLevelConfigurationModal = ({ integration, onClose, onPressOutside }: Props) => {
  const { closeModal } = useModals()
  const updateExternalIntegration = useUpdateExternalIntegration()
  const { data: externalIntegrationsData } = useExternalIntegrations()

  const currentIntegration =
    (externalIntegrationsData?.external_integrations?.find(
      (int) => int.id === integration.id,
    ) as GoHighLevelIntegration) || integration

  const profile = currentIntegration.additional_data as GoHighLevelProfile

  const availableBlogs = profile?.blogs || []
  const availableAuthors = profile?.authors || []

  const [selectedBlogId, setSelectedBlogId] = useState<string | undefined>(() => {
    if (profile?.selected_blog_id) {
      return profile.selected_blog_id
    }
    if (availableBlogs.length === 1) {
      return availableBlogs[0].id
    }
    return undefined
  })

  const [selectedAuthorId, setSelectedAuthorId] = useState<string | undefined>(() => {
    if (profile?.selected_author_id) {
      return profile.selected_author_id
    }
    if (availableAuthors.length === 1) {
      return availableAuthors[0].id
    }
    return undefined
  })

  useEffect(() => {
    if (profile?.selected_blog_id && profile.selected_blog_id !== selectedBlogId) {
      setSelectedBlogId(profile.selected_blog_id)
    }
    if (profile?.selected_author_id && profile.selected_author_id !== selectedAuthorId) {
      setSelectedAuthorId(profile.selected_author_id)
    }
  }, [profile?.selected_blog_id, profile?.selected_author_id]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = () => {
    if (onClose) {
      onClose()
    } else {
      closeModal()
    }
  }

  const handleSaveDefaults = async () => {
    if (!selectedBlogId || !selectedAuthorId) {
      showToastMessage('Please select both a blog and an author', 'error')
      return
    }

    try {
      await updateExternalIntegration.mutateAsync({
        id: currentIntegration.id,
        additional_data: {
          ...profile,
          selected_blog_id: selectedBlogId,
          selected_author_id: selectedAuthorId,
        },
      })

      showToastMessage('Default blog and author saved successfully!', 'success')
      handleClose()
    } catch (error) {
      showToastMessage('Failed to save defaults. Please try again.', 'error')
    }
  }

  const canSave = selectedBlogId && selectedAuthorId
  const hasOptions = availableBlogs.length > 0 && availableAuthors.length > 0

  return (
    <Modal.Root size="sm" onPressOutside={() => {}} onClose={() => {}} aria-label="Configure GoHighLevel Defaults">
      <Modal.Content>
        <div className={Styles.content}>
          {hasOptions ? (
            <div className={Styles.selectionContainer}>
              <div className={Styles.header}>
                <Heading level={2}>GoHighLevel Integration Settings</Heading>
              </div>
              <div className={Styles.fieldContainer}>
                <Heading level={4}>Default blog for all blog posts</Heading>
                <GoHighLevelBlogSelect
                  blogs={availableBlogs}
                  selectedBlogId={selectedBlogId}
                  onBlogSelect={setSelectedBlogId}
                  placeholder="Select a default blog"
                />
              </div>

              <div className={Styles.fieldContainer}>
                <Heading level={4}>Default author for all blog posts</Heading>
                <GoHighLevelAuthorSelect
                  authors={availableAuthors}
                  selectedAuthorId={selectedAuthorId}
                  onAuthorSelect={setSelectedAuthorId}
                  placeholder="Select a default author"
                />
              </div>
            </div>
          ) : (
            <div className={Styles.noOptionsMessage}>
              <Text>
                No blogs or authors were found in your GoHighLevel account. Create them in GoHighLevel, then return here
                and refresh your integration.
              </Text>
            </div>
          )}
        </div>
      </Modal.Content>

      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Modal.FooterButton variant="tertiary" onPress={handleClose}>
            Cancel
          </Modal.FooterButton>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          {hasOptions ? (
            <Modal.FooterButton
              variant="primary"
              onPress={handleSaveDefaults}
              isDisabled={!canSave || updateExternalIntegration.isLoading}
            >
              {updateExternalIntegration.isLoading ? 'Saving...' : 'Save Changes'}
            </Modal.FooterButton>
          ) : (
            <Modal.FooterButton variant="primary" onPress={handleClose}>
              Save Changes
            </Modal.FooterButton>
          )}
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}
